import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import type { EventAccountListItem } from './EventAccountStockSection';
import { StockStatCard } from './StockStatCard';
import { ItemTypeCategory } from '@/graphql/generatedTypes';
import {
  EventAccountStockSectionV2,
  StockEntryV2,
} from './EventAccountStockSectionV2';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search } from 'lucide-react';
import { GetEventQuery } from '@/graphql/queries/getEvent.generated';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useGetEventFulfillmentDataByFulfillmentStepV2Query } from '@/graphql/queries/getEventFulfillmentDataByFulfillmentStepV2.generated';
import { useAllItemTypesQuery } from '@/graphql/queries/allItemTypes.generated';

interface EventStockOverviewV2Props {
  event: GetEventQuery['event'] | undefined;
}

interface ContainerTypeInfo {
  id: number;
  name: string;
  image_url?: string | null;
  packing_unit: number;
  color_hex_code?: string | null;
}

// Component to handle individual event account stock
function EventAccountStockSectionWithData({
  eventAccount,
  stockEntries,
  containerTypes,
  isLoading,
}: {
  eventAccount: EventAccountListItem;
  eventId: string;
  stockEntries: StockEntryV2[];
  containerTypes: ContainerTypeInfo[];
  isLoading: boolean;
}) {
  // Filter stock entries for this event account
  const accountStockEntries = stockEntries.filter(
    (entry) => entry.event_account_id === eventAccount.id,
  );

  return (
    <EventAccountStockSectionV2
      eventAccount={eventAccount}
      stockEntries={accountStockEntries}
      containerTypes={containerTypes}
      isLoading={isLoading}
    />
  );
}

export function EventStockOverviewV2({ event }: EventStockOverviewV2Props) {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();

  // Fetch stock data from GraphQL V2
  const {
    data: stockDataResponse,
    loading: isLoadingAllStock,
    error: stockError,
  } = useGetEventFulfillmentDataByFulfillmentStepV2Query({
    variables: { event_id: eventId || '' },
    skip: !eventId,
  });

  // Fetch container station types (item types with CONTAINER_STATION category)
  const { data: itemTypesData } = useAllItemTypesQuery({
    variables: {
      filter: { category: ItemTypeCategory.ContainerStation },
      take: 1000,
      skip: 0,
    },
  });

  // Map GraphQL response to component data structures
  const stockEntries = useMemo<StockEntryV2[]>(() => {
    if (
      !stockDataResponse?.getEventFulfillmentDataByFulfillmentStepV2
        ?.stock_entries
    ) {
      return [];
    }
    return stockDataResponse.getEventFulfillmentDataByFulfillmentStepV2.stock_entries.map(
      (entry) => ({
        event_account_id: entry.event_account_id,
        container_type_id: entry.container_type_id ?? null,
        container_station_type_id: entry.container_station_type_id ?? null,
        total_container_stations_ordered:
          entry.total_container_stations_ordered,
        total_container_stations_delivered:
          entry.total_container_stations_delivered,
        total_container_stations_in_stock:
          entry.total_container_stations_in_stock,
        total_container_stations_returned:
          entry.total_container_stations_returned,
        total_container_stations_returned_based_on_history:
          entry.total_container_stations_returned_based_on_history,
        total_containers_ordered: entry.total_containers_ordered,
        total_containers_delivered: entry.total_containers_delivered,
        total_containers_in_stock: entry.total_containers_in_stock,
        total_containers_returned: entry.total_containers_returned,
      }),
    );
  }, [stockDataResponse]);

  const containerTypes = useMemo<ContainerTypeInfo[]>(() => {
    const types: ContainerTypeInfo[] = [];

    // Add container types from the V2 query
    stockDataResponse?.getEventFulfillmentDataByFulfillmentStepV2?.container_types?.forEach(
      (ct) => {
        types.push({
          id: ct.id,
          name: ct.name,
          image_url: ct.image_url ?? null,
          packing_unit: ct.packing_unit ?? 1,
          color_hex_code: ct.color_hex_code ?? null,
        });
      },
    );

    // Add container station types from the item types query
    itemTypesData?.itemTypes?.items?.forEach((it) => {
      types.push({
        id: it.id,
        name: it.name,
        image_url: it.image_url ?? null,
        packing_unit: 1,
        color_hex_code: it.color_hex_code ?? null,
      });
    });

    return types;
  }, [stockDataResponse, itemTypesData]);

  // Event accounts sorted by total container station deliveries (highest first)
  const eventAccounts = useMemo(() => {
    const data = stockDataResponse?.getEventFulfillmentDataByFulfillmentStepV2;
    if (!data?.event_accounts) {
      return [];
    }

    // Pre-compute total deliveries per event account for sorting
    const deliveriesByAccount = new Map<string, number>();
    data.stock_entries?.forEach((entry) => {
      deliveriesByAccount.set(
        entry.event_account_id,
        (deliveriesByAccount.get(entry.event_account_id) ?? 0) +
          entry.total_container_stations_delivered,
      );
    });

    const accounts: EventAccountListItem[] = data.event_accounts.map((ea) => ({
      id: ea.id,
      name: ea.name ?? null,
    }));

    accounts.sort(
      (a, b) =>
        (deliveriesByAccount.get(b.id) ?? 0) -
        (deliveriesByAccount.get(a.id) ?? 0),
    );

    return accounts;
  }, [stockDataResponse]);

  // Filter event accounts based on search query
  const filteredEventAccounts = useMemo(() => {
    if (!searchQuery.trim()) return eventAccounts;
    const query = searchQuery.toLowerCase();
    return eventAccounts.filter(
      (account) =>
        account.name?.toLowerCase().includes(query) ||
        account.id.toLowerCase().includes(query),
    );
  }, [eventAccounts, searchQuery]);

  // Aggregated stats directly from stock_event_summary
  const aggregatedStats = useMemo(() => {
    const summary =
      stockDataResponse?.getEventFulfillmentDataByFulfillmentStepV2
        ?.stock_event_summary;
    if (!summary) {
      return {
        sealedWarehouse: null,
        handedOut: null,
        returned: null,
        atItemLocation: null,
      };
    }
    return {
      sealedWarehouse: summary.sealed_container_stations_at_warehouse,
      handedOut: summary.handed_out_container_stations,
      returned: summary.returned_container_stations,
      atItemLocation:
        summary.container_stations_currently_at_event_item_locations,
    };
  }, [stockDataResponse]);

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col',
        isMobile ? 'tw:gap-6' : 'tw:container tw:gap-8',
      )}
    >
      <h1
        className={cn(
          'tw:font-semibold tw:leading-none tw:tracking-[-0.4px] tw:text-black',
          isMobile ? 'tw:text-[24px]' : 'tw:text-[40px]',
        )}
      >
        Stock overview
      </h1>

      <div
        className={cn(
          'tw:flex tw:flex-col',
          isMobile ? 'tw:gap-6' : 'tw:gap-8',
        )}
      >
        {/* Stat Cards */}
        <div
          className={cn(
            'tw:flex tw:gap-2 tw:items-start',
            isMobile ? 'tw:flex-wrap' : '',
          )}
        >
          <StockStatCard
            type="sealed-warehouse"
            value={aggregatedStats.sealedWarehouse}
            className={
              isMobile ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]' : 'tw:w-[140px]'
            }
          />
          <StockStatCard
            type="handed-out"
            value={aggregatedStats.handedOut}
            className={
              isMobile ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]' : 'tw:w-[140px]'
            }
          />
          <StockStatCard
            type="returned"
            value={aggregatedStats.returned}
            className={
              isMobile ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]' : 'tw:w-[140px]'
            }
          />
          <StockStatCard
            type="at-item-location"
            value={aggregatedStats.atItemLocation}
            className={
              isMobile ? 'tw:flex-1 tw:min-w-[calc(50%-4px)]' : 'tw:w-[140px]'
            }
          />
        </div>

        {/* Search Input */}
        <div
          className={cn('tw:relative', isMobile ? 'tw:w-full' : 'tw:w-[230px]')}
        >
          <Search className="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:size-5 tw:text-neutral-500" />
          <Input
            placeholder="Search event account name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="tw:pl-10 tw:bg-neutral-100 tw:border-neutral-300 tw:min-h-[36px]"
          />
        </div>

        {/* Event Account Sections */}
        <div className="tw:flex tw:flex-col tw:gap-4">
          {filteredEventAccounts.length === 0 ? (
            isLoadingAllStock ? (
              <>
                <Skeleton className="tw:h-16 tw:w-full tw:rounded-lg" />
                <Skeleton className="tw:h-16 tw:w-full tw:rounded-lg" />
              </>
            ) : (
              <div className="tw:p-8 tw:text-center tw:text-neutral-500">
                {searchQuery
                  ? 'No event accounts found matching your search'
                  : 'No event accounts available'}
              </div>
            )
          ) : (
            filteredEventAccounts.map((account) => (
              <EventAccountStockSectionWithData
                key={account.id}
                eventAccount={account}
                eventId={eventId || ''}
                stockEntries={stockEntries}
                containerTypes={containerTypes}
                isLoading={isLoadingAllStock}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
